import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A "playable" Material Symbols icon: the animation replays on hover of its
 * closest button/link (or itself) and on click. Pure CSS keyframes (see
 * styles/experience.css), so it costs no animation library.
 *
 *   <AnimatedIcon name="notifications" play="ring" />
 *   <AnimatedIcon name="auto_awesome" play="sparkle" loop />
 *
 * play: ring | spin | pop | send | wiggle | bounce | sparkle
 */
export default function AnimatedIcon({ name, play = 'pop', loop = false, size = '1.25rem', style, className = '', filled = false }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  const trigger = useCallback(() => {
    setPlaying(false);
    // next frame so the same animation can restart
    requestAnimationFrame(() => setPlaying(true));
  }, []);

  useEffect(() => {
    const el = ref.current;
    const host = el && (el.closest('button, a, [role="button"]') || el);
    if (!host) return undefined;
    host.addEventListener('mouseenter', trigger);
    host.addEventListener('click', trigger);
    return () => {
      host.removeEventListener('mouseenter', trigger);
      host.removeEventListener('click', trigger);
    };
  }, [trigger]);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={`icon-play play-${play}${loop ? ' play-loop' : ''}${playing ? ' is-playing' : ''} ${className}`}
      onAnimationEnd={() => setPlaying(false)}
    >
      <span
        className="material-symbols-outlined"
        style={{ fontSize: size, verticalAlign: 'middle', lineHeight: 1,
                 fontVariationSettings: filled ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24" : undefined, ...style }}
      >{name}</span>
    </span>
  );
}
